import { flow as pipe } from 'lodash';

import {
  _batch,
  LocationManager,
  LocationManagerOptions,
} from './LocationManager';
import { SimpleType, Value } from './utils';
import { useQueryParamsConsumer } from './QueryParamsProvider';

export type QueryParams = ReturnType<typeof useQueryParams>;

type ParamsFunction = (key: string, value: SimpleType) => void;
type RemoveFunction = (key: string, value?: SimpleType) => void;
type PickFunction = (key: string | string[]) => void;
type SetFunction<Params> = (
  params?: Params,
  options?: { shouldReplace?: boolean }
) => void;
type HasFunction = (key: string, value?: SimpleType) => boolean;
type WithoutFunction = (keys: string | string[]) => void;
type GetFunction = <Val = Value>(key: string, defaultValue?: Value) => Val;
type ChangeFunction = (key: string, value: Value) => void;

const useQueryParams = <Params extends object>(
  options?: LocationManagerOptions
) => {
  const { history, location } = useQueryParamsConsumer();

  const locationManager = LocationManager<Params>(location, options);

  /**
   * Push params to history. If newUrl is same as current function does nothing.
   * @param {string} newUrl
   * @param {boolean} shouldReplace
   */
  const push = (shouldReplace: boolean = false) => (newUrl: string): string => {
    if (_batch) {
      return newUrl;
    }
    if (newUrl === locationManager.url) {
      return newUrl;
    }
    if (shouldReplace) {
      history.replace(newUrl);
      return newUrl;
    }
    history.push(newUrl);
    return newUrl;
  };

  /**
   * Add new param, if key already exist function crate array, if value already exist param does nothing.
   * @param {string} key
   * @param {SimpleType} value
   */
  const add: ParamsFunction = pipe(locationManager.add, push());

  /**
   * Change exist value. If value is undefined function remove key.
   * Useful for input operations
   * @param {string} key
   * @param {Value} [value]
   */
  const change: ChangeFunction = pipe(locationManager.change, push());

  /**
   * Remove param or param value. If value is simple type or value is not array then remove key.
   * If value not exist in prams then function does nothing.
   * @param {string} key
   * @param {SimpleType} [value]
   */
  const remove: RemoveFunction = pipe(locationManager.remove, push());

  /**
   * If value is set then remove value otherwise add value. If value is not array then remove key.
   * @param {string} key
   * @param {SimpleType} value
   */
  const toggle: ParamsFunction = pipe(locationManager.toggle, push());

  /**
   * Push params with picked keys, if key not exist function ignore it.
   * @param {string | string[]} key
   */
  const pick: PickFunction = pipe(locationManager.pick, push());

  /**
   * Parsed location.search, it reactive because of useLocation().
   * @params {object} Params
   */
  const set: SetFunction<Params> = pipe(locationManager.set, push());

  const extend: SetFunction<Params> = pipe(locationManager.extend, push());

  const batch = (fn: () => any[], shouldReplace?: boolean) => {
    push(shouldReplace)(locationManager.batch(fn));
  };

  /**
   * Helper for pagination things.
   */
  type Option = string | number;
  type RequiredOptions = {
    limit: Option;
    offset: Option;
  };
  type Options = RequiredOptions & { total: Option };
  type BasicPages = {
    current: number;
    change: (page: number, key?: string) => void;
  };
  type Pages = BasicPages & { total: number };

  function pages(options: Options): Pages;
  function pages(options: RequiredOptions): BasicPages;
  function pages({ limit, offset, total }: any): BasicPages | Pages {
    const limitN = Number(limit);
    const offsetN = Number(offset);
    const totalN = Number(total);

    if ([limitN, offsetN].some((val) => isNaN(val))) {
      throw Error(`Some value is not a number`);
    }

    if (total === undefined) {
      return {
        current: Math.floor(offsetN / limitN) + 1,
        change(page: number, key: string = 'offset'): void {
          change(key, String((page - 1) * limitN));
        },
      };
    }

    if (isNaN(totalN)) {
      throw Error('total is NaN');
    }

    return {
      current: Math.floor(offsetN / limitN) + 1,
      total: Math.ceil(totalN / limitN),
      change(page: number, key: string = 'offset'): void {
        change(key, String((page - 1) * limitN));
      },
    };
  }

  return {
    add,
    change,
    remove,
    toggle,
    pick,
    set,
    params: locationManager.params,
    url: locationManager.url,
    has: locationManager.has as HasFunction,
    without: locationManager.without as WithoutFunction,
    get: locationManager.get as GetFunction,
    pages,
    buildUrl: locationManager.buildUrl,
    batch,
    extend,
  };
};

export { useQueryParams };
